import React, { useCallback, useEffect, useState } from 'react';
import { ShoppingCartIcon, TrendingUpIcon, CalendarIcon, PackageIcon, BarChart3Icon, FileTextIcon, MapPinIcon, RefreshCwIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Pagination } from '../components/ui/Pagination';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { supabaseAdmin } from '../lib/supabase';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
interface SaleItem {
  id: string;
  clienteNombre: string;
  vendedorId: string;
  vendedorNombre: string;
  fecha: Date;
  zona: string;
  montoTotal: number;
  subTotal: number;
  itbis: number;
  referencia: string;
  comentarios: string;
  source: 'visita' | 'factura';
}
export function SalesPage() {
  const {
    user
  } = useAuth();
  const {
    visits,
    users,
    totalVendido: totalVendidoGlobal
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const canSeeAll = isAdmin || user?.permisos?.cuentas_cobrar === true;
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'visita' | 'factura'>('all');
  // Only actual vendedores (users with new-visit permission)
  const vendedorIds = new Set(users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.id));
  const vendedorNames = new Set(users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.nombre));
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // Load invoices from Supabase
  const [invoices, setInvoices] = useState<any[]>([]);
  const loadInvoices = useCallback(async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('invoices').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_emision,sub_total,itbis,monto,estado').not('estado', 'eq', 'anulada').order('fecha_emision', {
        ascending: false
      }).limit(2000);
      if (data) setInvoices(data);
      if (error) console.error('Error loading invoices for sales:', error);
    } catch {}
  }, []);
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);
  useRealtimeRefresh(['invoices'], loadInvoices);
  // Build unified sales list
  const visitSales: SaleItem[] = visits.filter(v => v.venta.huboVenta).map(v => {
    const montoTotal = v.venta.montoVenta;
    const subTotal = Math.round(montoTotal / 1.18 * 100) / 100;
    const itbis = Math.round((montoTotal - subTotal) * 100) / 100;
    return {
      id: `v-${v.id}`,
      clienteNombre: v.clienteNombre,
      vendedorId: v.vendedorId,
      vendedorNombre: v.vendedorNombre,
      fecha: new Date(v.fecha),
      zona: v.zona,
      montoTotal,
      subTotal,
      itbis,
      referencia: v.venta.numeroPedido || '',
      comentarios: v.comentarios || '',
      source: 'visita' as const
    };
  });
  const invoiceSales: SaleItem[] = invoices.map(inv => ({
    id: `f-${inv.id}`,
    clienteNombre: inv.farmacia,
    vendedorId: inv.vendedor_id || '',
    vendedorNombre: inv.vendedor || '',
    fecha: new Date(inv.fecha_emision),
    zona: '',
    montoTotal: inv.monto || 0,
    subTotal: inv.sub_total || 0,
    itbis: inv.itbis || 0,
    referencia: inv.numero_factura || '',
    comentarios: '',
    source: 'factura' as const
  }));
  const allSales = [...visitSales, ...invoiceSales].filter(s => canSeeAll || s.vendedorId === user?.id).filter(s => {
    const saleDate = s.fecha.toISOString().split('T')[0];
    if (dateFrom && saleDate < dateFrom) return false;
    if (dateTo && saleDate > dateTo) return false;
    return true;
  }).filter(s => sourceFilter === 'all' || s.source === sourceFilter).sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
  // ── Global totals — totalVendido from context (all invoices), counts from local data ──
  const totalVendido = totalVendidoGlobal;
  const allSalesUnfiltered = [...visitSales, ...invoiceSales].filter(s => canSeeAll || s.vendedorId === user?.id);
  const totalPedidos = allSalesUnfiltered.length;
  const promedioVenta = totalPedidos > 0 ? totalVendido / totalPedidos : 0;
  const today = new Date();
  const todaySales = allSales.filter(s => s.fecha.toDateString() === today.toDateString());
  const todayTotal = todaySales.reduce((sum, s) => sum + s.montoTotal, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => date.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short'
  });
  const totalPages = Math.ceil(allSales.length / ITEMS_PER_PAGE);
  const paginatedSales = allSales.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Map vendedorId -> canonical name from profiles
  const vendedorIdToName: Record<string, string> = {};
  users.filter(u => u.activo && u.permisos?.['new-visit'] === true).forEach(u => {
    vendedorIdToName[u.id] = u.nombre;
  });
  // Also map vendedorName variations -> canonical name (case-insensitive, trimmed)
  const vendedorNameNormalized: Record<string, string> = {};
  users.filter(u => u.activo && u.permisos?.['new-visit'] === true).forEach(u => {
    vendedorNameNormalized[u.nombre.trim().toLowerCase()] = u.nombre;
  });
  const salesByVendor = isAdmin ? allSales.filter(s => vendedorIds.has(s.vendedorId) || vendedorNames.has(s.vendedorNombre) || vendedorNameNormalized[s.vendedorNombre.trim().toLowerCase()]).reduce((acc, s) => {
    // Resolve canonical name: prefer ID match, then normalized name match
    const name = vendedorIdToName[s.vendedorId] || vendedorNameNormalized[s.vendedorNombre.trim().toLowerCase()] || s.vendedorNombre || 'Sin vendedor';
    if (!acc[name]) acc[name] = {
      total: 0,
      count: 0
    };
    acc[name].total += s.montoTotal;
    acc[name].count += 1;
    return acc;
  }, {} as Record<string, {
    total: number;
    count: number;
  }>) : {};
  return <div className="space-y-6" data-id="element-3689">
      <PageHeader title="Registro de Ventas" subtitle="Historial de ventas y facturación" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-3691" />} data-id="element-3690">
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap" data-id="element-3692">
          <div className="flex items-center gap-1.5" data-id="element-3693">
            <span className="text-xs text-slate-500 font-medium" data-id="element-3694">Desde</span>
            <input type="date" value={dateFrom} onChange={e => {
            setDateFrom(e.target.value);
            setCurrentPage(1);
          }} className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3695" />
          </div>
          <div className="flex items-center gap-1.5" data-id="element-3696">
            <span className="text-xs text-slate-500 font-medium" data-id="element-3697">Hasta</span>
            <input type="date" value={dateTo} onChange={e => {
            setDateTo(e.target.value);
            setCurrentPage(1);
          }} className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3698" />
          </div>
          <select value={sourceFilter} onChange={e => {
          setSourceFilter(e.target.value as any);
          setCurrentPage(1);
        }} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3699">
            <option value="all" data-id="element-3700">Todas las fuentes</option>
            <option value="visita" data-id="element-3701">Solo Visitas</option>
            <option value="factura" data-id="element-3702">Solo Facturas</option>
          </select>
          <Button variant="secondary" onClick={() => loadInvoices()} title="Recargar facturas" data-id="element-3703">
            <RefreshCwIcon className="w-4 h-4" data-id="element-3704" />
          </Button>
          {(dateFrom || dateTo || sourceFilter !== 'all') && <Button variant="secondary" onClick={() => {
          setDateFrom('');
          setDateTo('');
          setSourceFilter('all');
        }} data-id="element-3705">
              Limpiar
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-3706">
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-3708" />} variant="primary" data-id="element-3707" />
        <StatsCard title="Ventas Hoy" value={formatCurrency(todayTotal)} subtitle={`${todaySales.length} registros`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-3710" />} variant="success" data-id="element-3709" />
        <StatsCard title="Total Registros" value={totalPedidos} icon={<PackageIcon className="w-5 h-5" data-id="element-3712" />} variant="warning" data-id="element-3711" />
        <StatsCard title="Promedio por Venta" value={formatCurrency(promedioVenta)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-3714" />} variant="default" data-id="element-3713" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3715">
        {/* Sales list */}
        <Card padding="none" className="lg:col-span-2 animate-stagger-2" data-id="element-3716">
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-3717">
            <CardHeader title="Historial de Ventas" subtitle={`${allSales.length} ventas registradas`} data-id="element-3718" />
          </div>

          {allSales.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-3719">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3720">
                <ShoppingCartIcon className="w-8 h-8 text-slate-400" data-id="element-3721" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-3722">
                No hay ventas
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-3723">
                No hay ventas registradas en el período seleccionado
              </p>
            </div> : <>
              <div className="divide-y divide-slate-100" data-id="element-3724">
                {paginatedSales.map(sale => <div key={sale.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-3725">
                    <div className="flex items-start justify-between" data-id="element-3726">
                      <div className="flex-1" data-id="element-3727">
                        <div className="flex items-center gap-3" data-id="element-3728">
                          <h4 className="font-semibold text-slate-900" data-id="element-3729">
                            {sale.clienteNombre}
                          </h4>
                          {sale.referencia && <Badge variant={sale.source === 'factura' ? 'warning' : 'info'} data-id="element-3730">
                              {sale.referencia}
                            </Badge>}
                          <Badge variant={sale.source === 'factura' ? 'default' : 'success'} data-id="element-3731">
                            {sale.source === 'factura' ? <span className="flex items-center gap-1" data-id="element-3732">
                                <FileTextIcon className="w-3 h-3" data-id="element-3733" />
                                Factura
                              </span> : <span className="flex items-center gap-1" data-id="element-3734">
                                <MapPinIcon className="w-3 h-3" data-id="element-3735" />
                                Visita
                              </span>}
                          </Badge>
                        </div>
                        <div className="mt-1 flex items-center gap-4 text-sm text-slate-500" data-id="element-3736">
                          {(isAdmin || canSeeAll) && sale.vendedorNombre && <span className="font-medium" data-id="element-3737">
                              {sale.vendedorNombre}
                            </span>}
                          <span data-id="element-3738">{formatDate(sale.fecha)}</span>
                          {sale.zona && <span data-id="element-3739">{sale.zona}</span>}
                        </div>
                        {sale.comentarios && <p className="mt-2 text-sm text-slate-600" data-id="element-3740">
                            {sale.comentarios}
                          </p>}
                      </div>
                      <div className="text-right flex-shrink-0 ml-4" data-id="element-3741">
                        <p className="text-xl font-bold text-[#2952C4]" data-id="element-3742">
                          {formatCurrency(sale.montoTotal)}
                        </p>
                        <div className="mt-1 text-xs text-slate-500 space-y-0.5" data-id="element-3743">
                          <p data-id="element-3744">
                            Sub Total:{' '}
                            <span className="font-semibold text-slate-700" data-id="element-3745">
                              {formatCurrency(sale.subTotal)}
                            </span>
                          </p>
                          <p data-id="element-3746">
                            ITBIS:{' '}
                            <span className="font-semibold text-slate-600" data-id="element-3747">
                              {formatCurrency(sale.itbis)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
              <div className="animate-stagger-3" data-id="element-3748">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={allSales.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-3749" />
              </div>
            </>}
        </Card>

        {/* Sales by vendor (admin only) */}
        {isAdmin && <Card className="animate-stagger-3" data-id="element-3750">
            <CardHeader title="Ventas por Vendedor" data-id="element-3751" />
            <div className="mt-4 space-y-4" data-id="element-3752">
              {Object.entries(salesByVendor).sort((a, b) => b[1].total - a[1].total).map(([vendedor, data]) => <div key={vendedor} className="p-4 bg-slate-50 rounded-xl" data-id="element-3753">
                    <div className="flex items-center justify-between mb-2" data-id="element-3754">
                      <div data-id="element-3755">
                        <p className="font-semibold text-slate-900" data-id="element-3756">
                          {vendedor}
                        </p>
                        <p className="text-sm text-slate-500" data-id="element-3757">
                          {data.count} ventas
                        </p>
                      </div>
                      <p className="text-lg font-bold text-[#2952C4]" data-id="element-3758">
                        {formatCurrency(data.total)}
                      </p>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-3759">
                      <div className="bg-[#2952C4] h-2 rounded-full transition-all" style={{
                width: `${data.total / Math.max(...Object.values(salesByVendor).map(d => d.total), 1) * 100}%`
              }} data-id="element-3760" />
                    </div>
                  </div>)}
            </div>
          </Card>}
      </div>
    </div>;
}